import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { fmtDate } from "../../lib/dateUtil";
import { HardHat, WarningCircle, ArrowSquareOut, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const PATH = { job: "jobs", emergency_job: "emergency-jobs" };

/** Jobs & emergency jobs that are dated or crewed but have no RAMS at all. Warn-only list with one-click "Create RAMS". */
export default function RamsReadinessCard({ compact = false, limit = 8 }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => API.get("/hs/rams/readiness").then(({ data }) => setData(data)).catch(() => setData({ count: 0, items: [] })), []);
  useEffect(() => { load(); }, [load]);

  if (!data || data.count === 0) return null;
  const rows = showAll ? data.items : data.items.slice(0, limit);

  const create = async (it) => {
    setBusy(it.id);
    try {
      const { data: r } = await API.post("/hs/rams", { entity_type: it.entity_type, entity_id: it.id });
      toast.success(`${r.reference} created for ${it.reference}`);
      navigate(`/health-safety/rams/${r.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-lg border-2 border-red-300 bg-red-50/70 dark:bg-red-950/20 p-4" data-testid="rams-readiness-card">
      <div className="flex items-start gap-3">
        <WarningCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" weight="fill" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold" data-testid="rams-readiness-count">{data.count} scheduled job{data.count === 1 ? "" : "s"} with no RAMS</p>
            {!compact && <Link to="/health-safety?tab=rams" className="text-xs text-primary hover:underline inline-flex items-center gap-1">H&amp;S Hub <ArrowSquareOut size={12} /></Link>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">Dated or crewed, but nobody has written a RAMS yet. Nothing is blocked — create one before the crew is on site.</p>
          <ul className="mt-2 divide-y divide-red-200/70 dark:divide-red-900/40">
            {rows.map(it => (
              <li key={`${it.entity_type}-${it.id}`} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`rams-readiness-row-${it.id}`}>
                <Link to={`/${PATH[it.entity_type]}/${it.id}`} className="font-mono font-semibold text-primary hover:underline shrink-0">{it.reference}</Link>
                {it.entity_type === "emergency_job" && <span className="rounded-full bg-red-600 text-white px-1.5 py-0.5 text-[9px] font-bold uppercase shrink-0">Emergency</span>}
                <span className="truncate flex-1 min-w-0">{it.title}{it.client_name ? <span className="text-muted-foreground"> · {it.client_name}</span> : null}</span>
                <span className="text-muted-foreground shrink-0 hidden sm:inline">{it.when ? fmtDate(it.when) : "No date"}{it.crew ? ` · ${it.crew}` : ""}</span>
                <Button size="sm" variant="outline" className="h-6 text-[11px] border-red-300 text-red-700 hover:bg-red-100 dark:text-red-200 shrink-0" disabled={busy === it.id} onClick={() => create(it)} data-testid={`rams-readiness-create-${it.id}`}>
                  <HardHat size={12} className="mr-1" /> {busy === it.id ? "Creating…" : "Create RAMS"}
                </Button>
              </li>
            ))}
          </ul>
          {data.items.length > limit && (
            <button type="button" onClick={() => setShowAll(v => !v)} className="mt-2 text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="rams-readiness-toggle">
              {showAll ? <><CaretUp size={12} /> Show fewer</> : <><CaretDown size={12} /> Show all {data.items.length}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
